/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import { DatasetOption } from 'src/hooks/apiResources/datasets';

/**
 * Removes unwanted schema prefixes from table names for better UX
 * Removes "public." prefix and other common default schemas
 * Preserves the actual table name including custom naming like "Testt:revenue"
 */
function cleanTableName(tableName: string, display_name: string, schema?: string): string {
  let cleanName = display_name || tableName;
  
  // Only remove schema prefixes from the table name if they exist
  const defaultSchemas = ['public', 'dbo', 'main', 'default'];
  
  for (const defaultSchema of defaultSchemas) {
    const prefix = `${defaultSchema}.`;
    if (cleanName.startsWith(prefix)) {
      cleanName = cleanName.substring(prefix.length);
      break;
    }
  }
  
  // DO NOT remove the colon part - preserve names like "Testt:revenue"
  // The colon is part of the actual dataset name and should be kept
  
  // Remove any leading/trailing whitespace
  cleanName = cleanName.trim();
  
  return cleanName;
}

/**
 * Converts SQL containing dataset names to use dataset macros
 * This function looks for dataset table names in SQL and converts them to {{ dataset(ID) }} syntax
 * Only replaces table names in table context (FROM, JOIN), not string literals in WHERE clauses
 */
export function convertDatasetNamesToMacros(sql: string, datasets: DatasetOption[]): string {
  if (!sql || !datasets || datasets.length === 0) {
    return sql;
  }

  let convertedSql = sql;

  // Sort datasets by table name length (descending) to handle longer names first
  const sortedDatasets = [...datasets].sort((a, b) => (b.display_name || b.table_name).length - (a.display_name || a.table_name).length);

  for (const dataset of sortedDatasets) {
    const { table_name, display_name, schema, id } = dataset;
    const macro = `{{ dataset(${id}) }}`;

    // Get ALL possible variations of how this dataset name can appear in SQL
    const allNameVariations = new Set<string>();
    
    // 1. Original table name (as stored in database)
    allNameVariations.add( display_name || table_name);
    
    // 2. Cleaned table name (what users see in dropdown)  
    const cleanName = cleanTableName(table_name, display_name, schema);
    const tableName = display_name || table_name
    if (cleanName !== tableName) {
      allNameVariations.add(cleanName);
    }
    
    // 3. With schema prefix (if schema exists)
    if (schema) {
      allNameVariations.add(`${schema}.${( display_name || table_name)}`);
      if (cleanName !== ( display_name || table_name)) {
        allNameVariations.add(`${schema}.${cleanName}`);
      }
    }
    
    // 4. With default schema prefixes that might be added/removed
    const defaultSchemas = ['public', 'dbo', 'main', 'default'];
    for (const defaultSchema of defaultSchemas) {
      allNameVariations.add(`${defaultSchema}.${( display_name || table_name)}`);
      if (cleanName !== ( display_name || table_name)) {
        allNameVariations.add(`${defaultSchema}.${cleanName}`);
      }
    }

    // Sort by length (longest first) to avoid partial replacements
    const sortedVariations = Array.from(allNameVariations)
      .filter(nameVariation => nameVariation && nameVariation.length > 0)
      .sort((a, b) => b.length - a.length);
    
    // Replace each variation with the macro, but only in table contexts
    for (const nameVariation of sortedVariations) {
      // Escape special regex characters
      const escapedName = nameVariation.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      
      // Create regex patterns that match table names in SQL contexts only
      // Match: FROM tablename, JOIN tablename, UPDATE tablename, INSERT INTO tablename
      // Don't match: WHERE col = 'tablename' or similar string literals
      
      const patterns = [
        // Quoted versions (exact matches for quoted identifiers)
        new RegExp(`\\b(FROM|JOIN|UPDATE|INSERT\\s+INTO)\\s+"${escapedName}"`, 'gi'),
        new RegExp(`\\b(FROM|JOIN|UPDATE|INSERT\\s+INTO)\\s+'${escapedName}'`, 'gi'),
        new RegExp(`\\b(FROM|JOIN|UPDATE|INSERT\\s+INTO)\\s+\`${escapedName}\``, 'gi'),
        
        // Unquoted version (word boundary to avoid partial matches)
        new RegExp(`\\b(FROM|JOIN|UPDATE|INSERT\\s+INTO)\\s+${escapedName}\\b`, 'gi')
      ];
      
      for (const pattern of patterns) {
        convertedSql = convertedSql.replace(pattern, (match, sqlKeyword) => {
          return `${sqlKeyword} ${macro}`;
        });
      }
    }
  }

  return convertedSql;
}

/**
 * Generates user-friendly SQL for a selected dataset
 * Shows dataset names that users can read and understand, with clean names
 */
export function generateDatasetSQL(dataset: DatasetOption): string {
  // Use cleaned dataset name for better user experience
  const cleanName = cleanTableName(dataset.table_name,dataset.display_name, dataset.schema);
  
  // For virtual datasets (those with SQL), we can optionally show a more descriptive comment
  const comment = dataset.sql 
    ? `-- Virtual dataset: ${cleanName}\n`
    : `-- Dataset: ${cleanName}\n`;
    
  // Determine the final table name to use in the SQL
  // Only include schema if it's not a default schema and provides value
  let tableName = cleanName;
  if (dataset.schema && 
      !['public', 'dbo', 'main', 'default'].includes(dataset.schema) && 
      !cleanName.includes('.')) {
    tableName = `${dataset.schema}.${cleanName}`;
  }
    
  return `${comment}SELECT * FROM ${tableName};`;
}

/**
 * Checks if SQL contains dataset macro syntax
 */
export function hasMacroSyntax(sql: string): boolean {
  return /\{\{\s*dataset\s*\(\s*\d+\s*\)\s*\}\}/i.test(sql);
}

/**
 * Converts dataset macros back to readable names for display
 * This is the reverse operation of convertDatasetNamesToMacros
 */
export function convertMacrosToDatasetNames(sql: string, datasets: DatasetOption[]): string {
  let convertedSql = sql;
  
  for (const dataset of datasets) {
    const { table_name, display_name, schema, id } = dataset;
    const cleanName = cleanTableName(table_name, display_name, schema);
    const macro = `{{ dataset(${id}) }}`;
    
    // Replace all macro occurrences with the clean dataset name
    convertedSql = convertedSql.replaceAll(macro, cleanName);
  }
  
  return convertedSql;
}

/**
 * Prepares SQL for execution by converting dataset names to macros
 * This should be called before sending SQL to the execution API
 */
export function prepareSQLForExecution(sql: string, datasets: DatasetOption[]): string {
  // If the SQL already contains macros, don't convert
  if (hasMacroSyntax(sql)) {
    return sql;
  }
  
  // Convert dataset names to macros for execution
  return convertDatasetNamesToMacros(sql, datasets);
}

/**
 * Utility function to fetch datasets and convert macros to readable names
 * This consolidates the dataset fetching and conversion logic
 */
export async function convertMacrosToNamesWithFetch(sql: string): Promise<string> {
  // If no macros, return original SQL
  if (!hasMacroSyntax(sql)) {
    return sql;
  }

  try {
    // Dynamically import SupersetClient to avoid circular dependency
    const { SupersetClient } = await import('@superset-ui/core');
    
    const response = await SupersetClient.get({
      endpoint: '/api/v1/dataset/',
      parseMethod: 'json',
    });

    if (response?.json?.result) {
      return convertMacrosToDatasetNames(sql, response.json.result);
    }
    
    return sql;
  } catch (error) {
    console.warn('Could not fetch datasets for macro conversion:', error);
    return sql;
  }
}
